"use client";

import React from "react";
import { ArrowRight, ShieldCheck, Layers, Factory, BookOpen } from "lucide-react";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
interface NavCard {
  title: string;
  desc: string;
  href: string;
}
interface ExploreMoreContent {
  eyebrow: string;
  heading: string;
  quality: NavCard;
  materials: NavCard;
  applications: NavCard;
  resources: NavCard;
  hometown: NavCard;
}
const copy: Record<"en" | "zh" | "es" | "de" | "fr" | "pt", ExploreMoreContent> = {
  en: {
    eyebrow: "Explore More",
    heading: "Continue Exploring",
    quality: {
      title: "Quality & Supply",
      desc: "How we approach specification control and supply.",
      href: "/QualitySupplyPage"
    },
    materials: {
      title: "Materials & Surface Finishes",
      desc: "Stainless steel grades and finishes for filtration mesh.",
      href: "/MaterialsAndSurfaceFinishesPage"
    },
    applications: {
      title: "Applications",
      desc: "Where our mesh is used across industries.",
      href: "/ApplicationsPage"
    },
    resources: {
      title: "Technical Resources",
      desc: "Guides, charts, standards and calculators.",
      href: "/TechnicalResourcesPage"
    },
    hometown: {
      title: "Our Hometown: Anping, Hebei",
      desc: "Where wire mesh making has deep roots.",
      href: "/OurHometownPage"
    }
  },
  zh: {
    eyebrow: "深入探索",
    heading: "继续了解更多",
    quality: {
      title: "质量与供应",
      desc: "我们如何管理规格管控与稳定供应体系。",
      href: "/QualitySupplyPage"
    },
    materials: {
      title: "材质与表面处理",
      desc: "适用于过滤丝网的不锈钢材质等级与表面工艺。",
      href: "/MaterialsAndSurfaceFinishesPage"
    },
    applications: {
      title: "应用领域",
      desc: "我们的丝网产品在各工业领域中的应用分布。",
      href: "/ApplicationsPage"
    },
    resources: {
      title: "技术资源",
      desc: "技术指南、规格图表、执行标准与计算工具。",
      href: "/TechnicalResourcesPage"
    },
    hometown: {
      title: "丝网之乡：河北安平",
      desc: "丝网制造产业渊源深厚之地。",
      href: "/OurHometownPage"
    }
  },
  es: {
    eyebrow: "Explorar más",
    heading: "Continúe explorando",
    quality: {
      title: "Calidad y suministro",
      desc: "Cómo enfocamos el control de especificaciones y el suministro.",
      href: "/QualitySupplyPage"
    },
    materials: {
      title: "Materiales y acabados superficiales",
      desc: "Grados de acero inoxidable y acabados para mallas filtrantes.",
      href: "/MaterialsAndSurfaceFinishesPage"
    },
    applications: {
      title: "Aplicaciones",
      desc: "Dónde se utiliza nuestra malla en diversos sectores industriales.",
      href: "/ApplicationsPage"
    },
    resources: {
      title: "Recursos técnicos",
      desc: "Guías, tablas, normas y calculadoras técnicas.",
      href: "/TechnicalResourcesPage"
    },
    hometown: {
      title: "Nuestra ciudad natal: Anping, Hebei",
      desc: "Donde la fabricación de malla metálica tiene profundas raíces.",
      href: "/OurHometownPage"
    }
  },
  de: {
    eyebrow: "Mehr entdecken",
    heading: "Entdecken Sie weiter",
    quality: {
      title: "Qualität & Versorgung",
      desc: "Unser Ansatz für Spezifikationskontrolle und verlässliche Belieferung.",
      href: "/QualitySupplyPage"
    },
    materials: {
      title: "Werkstoffe & Oberflächen",
      desc: "Edelstahlsorten und Veredelungen für Filtrationsgewebe.",
      href: "/MaterialsAndSurfaceFinishesPage"
    },
    applications: {
      title: "Anwendungen",
      desc: "Einsatzbereiche unserer Gewebeprodukte in der Industrie.",
      href: "/ApplicationsPage"
    },
    resources: {
      title: "Technische Ressourcen",
      desc: "Leitfäden, Tabellen, Standards und Berechnungstools.",
      href: "/TechnicalResourcesPage"
    },
    hometown: {
      title: "Unsere Heimatstadt: Anping, Hebei",
      desc: "Wo die Herstellung von Drahtgewebe tief verwurzelt ist.",
      href: "/OurHometownPage"
    }
  },
  fr: {
    eyebrow: "En savoir plus",
    heading: "Poursuivre votre découverte",
    quality: {
      title: "Qualité et approvisionnement",
      desc: "Notre démarche de contrôle des spécifications et d'approvisionnement.",
      href: "/QualitySupplyPage"
    },
    materials: {
      title: "Matériaux et finitions de surface",
      desc: "Nuances d'acier inoxydable et finitions pour toiles filtrantes.",
      href: "/MaterialsAndSurfaceFinishesPage"
    },
    applications: {
      title: "Applications",
      desc: "Domaines d'utilisation de nos toiles à travers les industries.",
      href: "/ApplicationsPage"
    },
    resources: {
      title: "Ressources techniques",
      desc: "Guides, tableaux, normes et outils de calcul.",
      href: "/TechnicalResourcesPage"
    },
    hometown: {
      title: "Notre ville d'origine : Anping, Hebei",
      desc: "Là où la fabrication de toiles métalliques est profondément enracinée.",
      href: "/OurHometownPage"
    }
  },
  pt: {
    eyebrow: "Explorar mais",
    heading: "Continue explorando",
    quality: {
      title: "Qualidade e fornecimento",
      desc: "Nossa abordagem no controle de especificações e fornecimento.",
      href: "/QualitySupplyPage"
    },
    materials: {
      title: "Materiais e acabamentos de superfície",
      desc: "Graus de aço inoxidável e acabamentos para malhas de filtração.",
      href: "/MaterialsAndSurfaceFinishesPage"
    },
    applications: {
      title: "Aplicações",
      desc: "Onde nossas malhas são aplicadas nos setores industriais.",
      href: "/ApplicationsPage"
    },
    resources: {
      title: "Recursos técnicos",
      desc: "Guias, tabelas, normas e calculadoras técnicas.",
      href: "/TechnicalResourcesPage"
    },
    hometown: {
      title: "Nossa cidade natal: Anping, Hebei",
      desc: "Onde a fabricação de telas de arame tem raízes profundas.",
      href: "/OurHometownPage"
    }
  }
};
export default function ExploreMore() {
  const {
    lang
  } = useLanguage();
  const current = pick(lang, copy);
  const cards = [{
    ...current.quality,
    icon: ShieldCheck
  }, {
    ...current.materials,
    icon: Layers
  }, {
    ...current.applications,
    icon: Factory
  }, {
    ...current.resources,
    icon: BookOpen
  }, {
    ...current.hometown,
    icon: Factory
  }];
  return <section id="exploremore" data-nav="light" className="bg-muted/40 text-foreground border-b border-border mf-section">
      <div className="mf-container">
        {/* Section Header */}
        <Reveal>
          <div className="space-y-4 max-w-3xl mb-10">
            <div className="inline-flex items-center gap-2 border border-border bg-card px-3 py-1 mf-caption font-mono tracking-wider uppercase text-primary rounded-md w-fit">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              <span>{current.eyebrow}</span>
            </div>
            <h2 className="mf-h2 text-foreground font-default">
              {current.heading}
            </h2>
          </div>
        </Reveal>

        {/* 5-card Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {cards.map((card, idx) => {
          const Icon = card.icon;
          return <Reveal key={card.href} delay={Math.min(idx * 60, 300)}>
                <Link to={card.href} className="group flex flex-col justify-between h-full rounded-lg border border-border bg-card p-6 transition-colors duration-150 hover:border-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring mf-hover-lift">
                  <div className="space-y-4">
                    <div className="flex size-11 items-center justify-center rounded-md bg-muted text-primary border border-border transition-colors duration-150 group-hover:bg-primary group-hover:text-primary-foreground">
                      <Icon className="size-5" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="mf-h3 text-foreground transition-colors duration-150 group-hover:text-primary">
                        {card.title}
                      </h3>
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {card.desc}
                      </p>
                    </div>
                  </div>

                  <div className="pt-6 mt-4 border-t border-border flex items-center gap-1.5 text-sm font-medium text-primary">
                    <span>{card.title}</span>
                    <ArrowRight className="size-4" />
                  </div>
                </Link>
              </Reveal>;
        })}
        </div>
      </div>
    </section>;
}